import { H2 } from '@ahmdigital/ui';
import { head, isEmpty, map, size, sortBy, take, upperFirst } from 'lodash/fp';
import React, { useEffect, useState } from 'react';

import { ContentWrapper, ItemsWrapper, StyledItemCard, StyledTabGroup } from './styles';
import { ProductsByType } from '../../../pages/insurance/types';
import { ProductType } from '../../../types/sanity';
import uncapFpIterator from '../../../../utils/uncap-fp-iterator';
import WrappedSection from '../wrapped-section';

type Props = {
  content: ProductsByType[] | null;
  title: string | null;
};

type PanelGroup = {
  panel: JSX.Element;
  text: string;
  value: string;
};

const mapUncapped = uncapFpIterator(map);

const ItemSection = ({ content, title }: Props) => {
  const [tabValue, setTabValue] = useState<string | null>(null);

  // Ensure we only take two item groups
  const itemGroups = take(2, content);
  const defaultTabGroup = head(itemGroups);
  const displayTab = size(itemGroups) > 1;

  useEffect(() => {
    if (defaultTabGroup) {
      setTabValue(defaultTabGroup.productType);
    }
  }, [defaultTabGroup, defaultTabGroup?.productType]);

  if (isEmpty(content)) {
    return null;
  }

  const itemPanels: PanelGroup[] = map(
    (item) => ({
      panel: (
        <ItemsWrapper>
          {mapUncapped(
            (product: ProductType, index: number) => (
              <StyledItemCard
                {...product}
                ariaLabel={product.name}
                index={index}
                key={index}
                includedServices={product?.includedServices}
              />
            ),
            sortBy((product) => product?.isBestSeller !== true, item.products),
          )}
        </ItemsWrapper>
      ),
      text: upperFirst(item?.productType),
      value: item?.productType,
    }),
    itemGroups,
  );

  return (
    <WrappedSection withWide withBorder>
      <ContentWrapper>
        <H2 variant={['center', 'marginBottomSpacer8']}>{title}</H2>
        <StyledTabGroup
          displayTab={displayTab}
          input={{
            onChange: setTabValue,
            value: tabValue,
          }}
          options={itemPanels}
        />
      </ContentWrapper>
    </WrappedSection>
  );
};

export default ItemSection;
